import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Building2, Home, Maximize2, Users, Briefcase, TrendingUp, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardOverview() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Properties', value: stats?.total_properties || 0, icon: Building2, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Total Plots', value: stats?.total_plots || 0, icon: Maximize2, color: 'text-green-500', bg: 'bg-green-50' },
    { label: 'Total Houses', value: stats?.total_houses || 0, icon: Home, color: 'text-purple-500', bg: 'bg-purple-50' },
    { label: 'Commercial', value: stats?.total_commercial || 0, icon: Building2, color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'Available', value: stats?.available_properties || 0, icon: TrendingUp, color: 'text-teal-500', bg: 'bg-teal-50' },
    { label: 'Total Leads', value: stats?.total_leads || 0, icon: Users, color: 'text-pink-500', bg: 'bg-pink-50' },
    { label: 'New Leads', value: stats?.new_leads || 0, icon: Users, color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'Active Jobs', value: stats?.active_jobs || 0, icon: Briefcase, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  ];

  return (
    <div data-testid="dashboard-overview">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">Overview of your properties and leads</p>
        </div>
        <Link to="/dashboard/properties/new">
          <Button className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-property-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Leads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Leads</CardTitle>
          <Link to="/dashboard/leads">
            <Button variant="outline" size="sm">View All</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Mobile</th>
                    <th className="text-left p-3">Interest</th>
                    <th className="text-left p-3">Source</th>
                    <th className="text-left p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => (
                    <tr key={lead.id} className="border-t">
                      <td className="p-3">{lead.name}</td>
                      <td className="p-3">{lead.mobile}</td>
                      <td className="p-3">{lead.property_type || lead.property_title || '-'}</td>
                      <td className="p-3">
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">{lead.source}</span>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          lead.status === 'new' ? 'bg-green-100 text-green-700' :
                          lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {lead.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No leads yet</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Link to="/dashboard/properties/new">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <Plus className="h-8 w-8 text-brand-orange mx-auto mb-2" />
              <h3 className="font-semibold">Add New Property</h3>
              <p className="text-sm text-gray-500">Create a new property listing</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/dashboard/jobs/new">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <Briefcase className="h-8 w-8 text-brand-orange mx-auto mb-2" />
              <h3 className="font-semibold">Post a Job</h3>
              <p className="text-sm text-gray-500">Add new job opening</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/dashboard/leads">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="p-6 text-center">
              <Users className="h-8 w-8 text-brand-orange mx-auto mb-2" />
              <h3 className="font-semibold">Manage Leads</h3>
              <p className="text-sm text-gray-500">View and update lead status</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
